import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Trophy, X } from 'lucide-react';
import { humanizeFamilyId } from '../../content';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';
interface NewlyUnlockedBannerProps {
  familyId: string | null;
  onClick: () => void;
  onClose: () => void;
}
/**
 * NewlyUnlockedBanner — meldt dat een uitdaging is vrijgespeeld.
 *
 * Twee dingen zaten hier fout en zijn hersteld:
 *
 * 1. De sluitknop was een `<div onClick>` GENEST BINNEN de grote knop. Dat is
 *    ongeldige interactieve nesting (een knop in een knop) én met het
 *    toetsenbord onbereikbaar: er was geen tabIndex en geen onKeyDown. Een kind
 *    dat met Tab werkt kon deze melding dus niet wegklikken. De twee acties
 *    staan nu als zusters naast elkaar in plaats van in elkaar.
 *
 * 2. De tekst zei "Je hebt 70% gehaald". Een percentage op een leerlingscherm
 *    gaat in tegen het beleid in `masteryLabelInfo.ts`, en het getal voegt hier
 *    niets toe: het kind hoeft niet te weten welke drempel het haalde, alleen
 *    dat er iets nieuws klaarstaat. Het nodigt wel uit tot vergelijken.
 */
export function NewlyUnlockedBanner({
  familyId,
  onClick,
  onClose
}: NewlyUnlockedBannerProps) {
  // F-FEEL-9: celebrate the click — sound + haptic before opening the challenge
  const handleClick = () => {
    playSoundIntent('reward.streak');
    hapticManager.hapticMedium();
    onClick();
  };
  return <AnimatePresence data-id="element-3224" data-mp="11rq7htlozh5z">
      {familyId && <motion.div initial={{
      opacity: 0,
      y: -20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -20,
      scale: 0.95
    }} transition={{
      type: 'spring',
      bounce: 0.4,
      duration: 0.28
    }} className="relative z-10" data-id="element-3225" data-mp="11qojkojkv06u">
        
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-1 rounded-2xl shadow-xl shadow-orange-500/20" data-id="element-3226" data-mp="1kizik7b42dzo">
            <div className="bg-white rounded-xl flex items-stretch relative overflow-hidden" data-id="element-3227" data-mp="3gnfbth83vry6">
              <div className="absolute -right-4 -top-4 text-orange-100 opacity-50 transform rotate-12 pointer-events-none" data-id="element-3228" data-mp="3bolvzi5cjouu">
                <Trophy className="w-24 h-24" aria-hidden="true" data-id="element-3229" data-mp="3qdrsuzgpp2zk" />
              </div>

              <button type="button" onClick={handleClick} className="flex-1 text-left p-4 flex items-center gap-4 rounded-xl hover:bg-orange-50/60 active:scale-[0.99] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2" data-id="element-3230" data-mp="3lk5qq186chi0">
              
                <span className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-sm" data-id="element-3231" data-mp="d5f0bs3cd4ft">
                  <Trophy className="w-6 h-6 text-orange-500" aria-hidden="true" data-id="element-3232" data-mp="2exaa2261eyve" />
                
                </span>

                <span className="flex-1 relative z-10 block" data-id="element-3233" data-mp="29z0brip0ni5k">
                  <span className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-0.5 flex items-center gap-1" data-id="element-3234" data-mp="2a0judfy5a5ai">
                    <Sparkles className="w-3 h-3" aria-hidden="true" data-id="element-3235" data-mp="ufsmcee1wbwj" />
                    Uitdaging ontgrendeld!
                  </span>
                  <span className="block text-slate-800 font-medium text-sm md:text-base" data-id="element-3236" data-mp="fptsgntj3mmv">
                    Je bent goed op weg met{' '}
                    <span className="font-bold" data-id="element-3237" data-mp="6pommi3zgwat">
                      {humanizeFamilyId(familyId)}
                    </span>
                    . Klik hier om de uitdaging te starten!
                  </span>
                </span>
              </button>

              <button type="button" onClick={onClose} aria-label="Deze melding sluiten" className="relative z-10 w-11 h-11 self-start m-1.5 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" data-id="element-3238" data-mp="3syg18r256dz4">
              
                <X className="w-5 h-5" aria-hidden="true" data-id="element-3239" data-mp="3s7hmxp0h6ce4" />
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}